import React, { useEffect, useState } from "react";
import { IColumn, IDetailsListProps, ShimmeredDetailsList } from "@fluentui/react";

interface ISortableDetailsListProps<T> extends IDetailsListProps {
  items: T[];
  columns: IColumn[];
  initialSortColumn: string;
  enableSorting: boolean;
  enableShimmer?: boolean;
  ariaLabelForShimmer?: string;
}
export const SortableDetailsList = <T extends object>({
  items: initialItems,
  columns: initialColumns,
  initialSortColumn,
  enableSorting,
  enableShimmer = false,
  ariaLabelForShimmer,
  ...props
}: ISortableDetailsListProps<T> & IDetailsListProps) => {
  const [sortColumn, setSortColumn] = useState<string>(initialSortColumn);
  const [isSortDescending, setIsSortDescending] = useState<boolean>(false);

  const getSortedItems = <T extends object>(
    items: T[],
    columnKey: string,
    isDescending: boolean
  ): T[] => {
    if (!enableSorting) {
      return [];
    }
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }
    const sortedItems = [...items].sort((a, b) => {
      let aValue = getNestedValue(a, columnKey);
      let bValue = getNestedValue(b, columnKey);
      let aValueR = "";
      let bValueR = "";
      // If the column data is an array, compare the lengths
      if (Array.isArray(aValue) && Array.isArray(bValue)) {
        aValueR = String(aValue.length);
        bValueR = String(bValue.length);
      }
      // If the column data is an object (but not an array), compare the JSON string representations
      else {
        aValueR = String(aValue);
        bValueR = String(bValue);
      }
      if (aValueR !== undefined && bValueR !== undefined) {
        if (aValueR < bValueR) {
          return isDescending ? 1 : -1;
        }
        if (aValueR > bValueR) {
          return isDescending ? -1 : 1;
        }
      }
      return 0;
    });
    return sortedItems;
  };

  const sortedItems = getSortedItems<T>(initialItems, sortColumn, isSortDescending);

  function getNestedValue<T>(obj: T, path: string): any {
    return path.split(".").reduce((acc: any, part: string) => acc && acc[part], obj);
  }

  const [sortedColumns, setSortedColumns] =
    React.useState<IColumn[]>(initialColumns); // Initial state is an empty array

  // Re-sync when the parent passes a new columns array (e.g. width overrides),
  // preserving the user-driven sort markers on matching column keys.
  useEffect(() => {
    setSortedColumns(prev =>
      initialColumns.map(col => {
        const existing = prev.find(p => p.key === col.key);
        return existing
          ? {
              ...col,
              isSorted: existing.isSorted,
              isSortedDescending: existing.isSortedDescending,
            }
          : col;
      })
    );
  }, [initialColumns]);

  const onColumnHeaderClick = (
    ev: React.MouseEvent<HTMLElement> | undefined,
    column: IColumn | undefined
  ): void => {
    const newSortColumn = column?.fieldName as string;
    const newIsSortDescending =
      sortColumn === column?.fieldName ? !isSortDescending : false;
    setSortColumn(newSortColumn);
    setIsSortDescending(newIsSortDescending);
    // Update the columns state
    const newColumns = initialColumns.map(col => {
      if (col.fieldName === column?.fieldName) {
        return {
          ...col,
          isSorted: true,
          isSortedDescending: newIsSortDescending,
        };
      } else {
        return {
          ...col,
          isSorted: false,
          isSortedDescending: false,
        };
      }
    });
    setSortedColumns(newColumns);
  };

  return (
    <ShimmeredDetailsList
      items={sortedItems}
      columns={sortedColumns}
      onColumnHeaderClick={onColumnHeaderClick}
      enableShimmer={enableShimmer}
      ariaLabelForShimmer={ariaLabelForShimmer}
      {...props}
    />
  );
}; // Add this closing curly brace
